import dayjs from "dayjs"
import type { TFunction } from "i18next"

import { budgetFormat } from "../../utils/number/budget-format"
import { surfaceColor } from "../../constants/surface-tokens"
import type { ContentFact, FactTone } from "./notification-content.types"

/** "Oct 1, 2026" — the format the whole feed states a business date in. */
export const CONTENT_DATE_FORMAT = "MMM D, YYYY"
export const CONTENT_DATETIME_FORMAT = "MMM D, YYYY · h:mm A"

export const toneColor: Record<FactTone, string> = {
  neutral: surfaceColor.inkMuted,
  warning: surfaceColor.atRiskInk,
  critical: surfaceColor.delayedInk,
  success: surfaceColor.onTrackInk
}

export const toneBackground: Record<FactTone, string> = {
  neutral: surfaceColor.chipBg,
  warning: surfaceColor.warningBg,
  critical: surfaceColor.criticalBg,
  success: surfaceColor.successBg
}

/**
 * A business date carries no time, so it is read as a plain calendar day.
 * Running it through the browser's zone is what turns "due Oct 1" into
 * "Sep 30" for anyone west of UTC.
 */
export const formatBusinessDate = (value: string | number) =>
  dayjs(String(value).slice(0, 10)).format(CONTENT_DATE_FORMAT)

/** An instant, which genuinely has a zone, shown in the viewer's. */
export const formatInstant = (value: string | number) =>
  dayjs(String(value)).tz().format(CONTENT_DATETIME_FORMAT)

/**
 * One fact as the reader sees it: a label and a value, both translated.
 *
 * The label comes from the fact's own key so a type the client has never seen
 * still renders something true rather than nothing — i18next falls back to
 * the key's humanised form.
 */
export function formatFact(fact: ContentFact, t: TFunction): { label: string; value: string } {
  const label = t(`notifications.facts.${fact.key}`, defaultLabel(fact.key))

  switch (fact.kind) {
    case "money": {
      const amount = budgetFormat(String(fact.value))
      if (!fact.direction) return { label, value: amount }
      // "$4,000 above contract value" reads as one statement, so the whole
      // comparison is the value and the fact's own label is not repeated.
      const against = fact.against
        ? t(`notifications.facts.${fact.against}`, defaultLabel(fact.against)).toLowerCase()
        : ""
      return {
        label: "",
        value: t(`notifications.facts.direction.${fact.direction}`, {
          amount,
          against,
          defaultValue:
            fact.direction === "above"
              ? "{{amount}} above {{against}}"
              : "{{amount}} below {{against}}"
        }).trim()
      }
    }
    case "date":
      return { label, value: formatBusinessDate(String(fact.value)) }
    case "datetime":
      return { label, value: formatInstant(String(fact.value)) }
    case "duration_days": {
      const days = Number(fact.value)
      return {
        label,
        value: t("notifications.facts.days", {
          count: days,
          defaultValue_one: "{{count}} day",
          defaultValue_other: "{{count}} days"
        })
      }
    }
    case "status":
      return { label, value: fact.label ?? String(fact.value) }
    case "count":
      return { label, value: String(fact.value) }
    case "list":
      return {
        label,
        value: Array.isArray(fact.value)
          ? fact.value.join(", ")
          : String(fact.value)
      }
    case "text":
    default:
      // A status rule names itself by key; the settings page holds its name.
      if (fact.key === "rule" && typeof fact.value === "string") {
        return {
          label,
          value: t(
            `settingsConfiguration.statusAutomation.rules.${fact.value.replace(/\./g, "_")}.name`,
            { defaultValue: fact.value }
          )
        }
      }
      return { label, value: String(fact.value) }
  }
}

/** `overdue_days` → "Overdue days", so an unknown key is still readable. */
function defaultLabel(key: string) {
  const words = key.replace(/_/g, " ")
  return words.charAt(0).toUpperCase() + words.slice(1)
}
